import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useApi } from "@/lib/api";
import { PageHeader, SectionTitle, PageLoader, StatusBadge, EmptyState, Btn, TextInput, TextArea } from "@/lib/ui";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { HelpCircle, Users, Briefcase, Plus, Pencil, Trash2, Eye, EyeOff, GripVertical } from "lucide-react";

const CONFIG = {
  faq: {
    title: "FAQ", kicker: "Website Content", icon: HelpCircle,
    primary: "question",
    fields: [
      { name: "question", label: "Question", type: "text", required: true },
      { name: "answer", label: "Answer", type: "textarea", required: true },
      { name: "category", label: "Category (optional)", type: "text" },
    ],
  },
  team: {
    title: "Team", kicker: "Website Content", icon: Users,
    primary: "name",
    fields: [
      { name: "name", label: "Full name", type: "text", required: true },
      { name: "role", label: "Role / title", type: "text", required: true },
      { name: "bio", label: "Short bio", type: "textarea" },
      { name: "photo_url", label: "Photo URL", type: "text" },
    ],
  },
  services: {
    title: "Services", kicker: "Website Content", icon: Briefcase,
    primary: "title",
    fields: [
      { name: "title", label: "Service title", type: "text", required: true },
      { name: "description", label: "Description", type: "textarea", required: true },
      { name: "image_url", label: "Image URL", type: "text" },
      { name: "price", label: "Price / from (optional)", type: "text" },
      { name: "cta_label", label: "Button label (optional)", type: "text" },
      { name: "cta_url", label: "Button link (optional)", type: "text" },
    ],
  },
};

function ItemDialog({ kind, cfg, item, onClose }) {
  const api = useApi();
  const qc = useQueryClient();
  const [form, setForm] = useState(() => {
    const base = { order: item?.order ?? 0, published: item?.published ?? false };
    cfg.fields.forEach((f) => (base[f.name] = item?.[f.name] ?? ""));
    return base;
  });
  const save = useMutation({
    mutationFn: () => item
      ? api.patch(`/cms/collection/${kind}/${item.id}`, form)
      : api.post(`/cms/collection/${kind}`, form),
    onSuccess: () => { toast.success(item ? "Updated" : "Created"); qc.invalidateQueries({ queryKey: ["cms-coll", kind] }); onClose(); },
    onError: (e) => toast.error(e.message),
  });
  const missing = cfg.fields.some((f) => f.required && !String(form[f.name] || "").trim());
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle>{item ? "Edit" : "New"} {cfg.title.replace(/s$/, "")}</DialogTitle></DialogHeader>
        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {cfg.fields.map((f) => (
            f.type === "textarea"
              ? <TextArea key={f.name} label={f.label} rows={3} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`${kind}-field-${f.name}`} />
              : <TextInput key={f.name} label={f.label} value={form[f.name]} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`${kind}-field-${f.name}`} />
          ))}
          <div className="flex items-center gap-4">
            <TextInput label="Order" type="number" value={form.order} onChange={(e) => setForm({ ...form, order: e.target.value })} data-testid={`${kind}-field-order`} className="w-28" />
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer mt-5" data-testid={`${kind}-field-published`}>
              <input type="checkbox" checked={form.published} onChange={(e) => setForm({ ...form, published: e.target.checked })} className="accent-[hsl(var(--primary))]" />
              Published (visible on website)
            </label>
          </div>
        </div>
        <DialogFooter>
          <Btn onClick={() => save.mutate()} disabled={save.isPending || missing} data-testid={`${kind}-save`}>{save.isPending ? "Saving…" : "Save"}</Btn>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function CmsCollection({ kind }) {
  const cfg = CONFIG[kind];
  const api = useApi();
  const qc = useQueryClient();
  const [editing, setEditing] = useState(undefined); // undefined=closed, null=new, obj=edit
  const { data: items = [], isLoading } = useQuery({ queryKey: ["cms-coll", kind], queryFn: () => api.get(`/cms/collection/${kind}`) });

  const toggle = useMutation({
    mutationFn: (it) => api.patch(`/cms/collection/${kind}/${it.id}`, { published: !it.published }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["cms-coll", kind] }),
    onError: (e) => toast.error(e.message),
  });
  const del = useMutation({
    mutationFn: (id) => api.del(`/cms/collection/${kind}/${id}`),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["cms-coll", kind] }); },
    onError: (e) => toast.error(e.message),
  });
  const Icon = cfg.icon;

  return (
    <div data-testid={`cms-${kind}-page`}>
      <PageHeader kicker={cfg.kicker} title={cfg.title}>
        <Icon className="h-5 w-5 text-primary" />
        <StatusBadge tone="muted">{items.length} item{items.length === 1 ? "" : "s"}</StatusBadge>
        <Btn onClick={() => setEditing(null)} data-testid={`${kind}-new`}><Plus className="h-4 w-4" /> New</Btn>
      </PageHeader>

      {isLoading ? <PageLoader /> : items.length === 0 ? (
        <EmptyState icon={Icon} title={`No ${cfg.title.toLowerCase()} yet`} hint="Create your first item — it appears on the GGG website once published." />
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="neu-card rounded-xl p-4 flex items-start gap-3" data-testid={`${kind}-item`}>
              <GripVertical className="h-4 w-4 text-muted-foreground mt-1 shrink-0" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-head font-semibold truncate">{it[cfg.primary] || "Untitled"}</span>
                  {it.published
                    ? <StatusBadge tone="ok" dot>Published</StatusBadge>
                    : <StatusBadge tone="muted">Draft</StatusBadge>}
                </div>
                {(it.answer || it.bio || it.description || it.role) && (
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{it.answer || it.bio || it.description || it.role}</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Btn size="sm" variant="ghost" onClick={() => toggle.mutate(it)} data-testid={`${kind}-toggle-${it.id}`} title={it.published ? "Unpublish" : "Publish"}>
                  {it.published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4 text-ok" />}
                </Btn>
                <Btn size="sm" variant="ghost" onClick={() => setEditing(it)} data-testid={`${kind}-edit-${it.id}`}><Pencil className="h-4 w-4" /></Btn>
                <AlertDialog>
                  <AlertDialogTrigger asChild><Btn size="sm" variant="ghost" data-testid={`${kind}-del-${it.id}`}><Trash2 className="h-4 w-4 text-err" /></Btn></AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader><AlertDialogTitle>Delete this item?</AlertDialogTitle><AlertDialogDescription>This cannot be undone.</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => del.mutate(it.id)}>Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing !== undefined && <ItemDialog kind={kind} cfg={cfg} item={editing} onClose={() => setEditing(undefined)} />}
    </div>
  );
}
